// Common
import DataTable from "@components/DataTable";
import type { ColumnDef } from "@tanstack/react-table";
import { useState } from "react";
import { Nav } from "react-bootstrap";
import { Button, Card, Form } from "react-bootstrap";
import { faTrashCan, faClockRotateLeft } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useSelector } from "react-redux";
import { RootState } from "@reducers/rootReducer";
import ConfirmationModal from "@components/Modals/ConfirmationModal";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import {
  useDeleteBusinessUnitMutation,
  useUpdateBusinessUnitMutation,
} from "@generated";
// BusinessUnit Specific
import useListBusinessUnits from "./api";
import { BusinessUnit } from "./types";

const List = (): JSX.Element => {
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [activeFilter, setActiveFilter] = useState<boolean>(true);
  const [filter, setFilter] = useState<{ active?: boolean }>({
    active: true,
  });
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const { accountSlug } = useSelector((state: RootState) => state.slug);
  const [updateBusinessUnit, {}] = useUpdateBusinessUnitMutation();
  const [deleteBusinessUnit, {}] = useDeleteBusinessUnitMutation();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [currentTab, setCurrentTab] = useState<string>("active");
  const [counts, setCounts] = useState({
    active: null,
    archived: null,
  });

  const { businessUnits, pageMeta, isLoading, refetch } =
    useListBusinessUnits({
      page: {
        number: pageNumber,
        size: pageSize,
      },
      filter,
    });

  const handleDelete = () => {
    setShowConfirmationModal(true);
  };

  const handleConfirm = () => {
    selectedIds.forEach((buId) => {
      if (currentTab == "active") {
        deleteBusinessUnit({
          slug: accountSlug,
          id: parseInt(buId),
        });
      } else if (currentTab == "archived") {
        let selected = businessUnits.find((bu) => bu.id == parseInt(buId));

        updateBusinessUnit({
          slug: accountSlug,
          id: parseInt(buId),
          body: {
            data: {
              id: parseInt(buId),
              type: "business-units",
              attributes: {
                name: selected?.name || "",
                active: true,
              },
            } as any,
          },
        });
      }
    });
    setSelectedIds([]);
    refetch();
    setShowConfirmationModal(false);
  };

  const handleCancel = () => {
    setShowConfirmationModal(false);
  };

  const filterByActive = (active: boolean) => {
    setFilter({ active });
  };

  const columns: ColumnDef<BusinessUnit>[] = [
    {
      header: "Name",
      cell(props) {
        return (
          <div
            className="businessUnitName"
            onClick={() => {
              window.location.assign(
                `/admin/business_units/${props.row.original.id}/edit`
              );
            }}
          >
            {props.row.original.name}
          </div>
        );
      },
    },
    {
      header: "External Name",
      cell(props) {
        return <div>{props.row.original.externalName}</div>;
      },
    },
    {
      header: "City",
      cell(props) {
        return <div>{props.row.original.city}</div>;
      },
    },
    {
      header: "State",
      cell(props) {
        return <div>{props.row.original.state}</div>;
      },
    },
  ];

  if (isLoading) {
    return <ScopeStackSpinner />;
  } else {
    return (
      <div className="businessUnitsTable">
        <Card
          style={{
            padding: "10px",
            borderRadius: "4px",
            border: "1px solid var(--gray-3-stroke, #D7D7D7)",
            background: " var(--white, #FFF)",
            boxShadow: "0px 2px 3px 0px rgba(0, 0, 0, 0.15)",
          }}
        >
          <Card.Header
            style={{
              backgroundColor: "#fff",
              lineHeight: "unset",
              display: "block",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div className="accordianTitle" style={{ margin: "0" }}>
                Business Units
              </div>
              <Button
                className="addBtn"
                onClick={() =>
                  window.location.assign("/admin/business_units/new")
                }
              >
                Add A Business Unit
              </Button>
            </div>
            <hr></hr>
            <div
              style={{
                display: "flex",
              }}
            >
              {selectedIds.length > 0 && (
                <Button
                  style={{
                    marginRight: "10px",
                    alignSelf: "flex-end",
                    background: "unset",
                    color: "var(--teal-1, #418172)",
                    fontWeight: "700",
                    width: "11em",
                    height: "40px",
                    borderRadius: "4px",
                    border: "1px solid var(--gray-3-stroke, #D7D7D7)",
                  }}
                  onClick={handleDelete}
                >
                  {selectedIds.length} Selected{" "}
                  <span
                    style={{
                      borderLeft: "1px solid #D7D7D7",
                      height: "24px",
                      margin: "0 8px",
                    }}
                  ></span>
                  {currentTab == "active" && (
                    <FontAwesomeIcon
                      style={{ color: "black" }}
                      icon={faTrashCan}
                    />
                  )}
                  {currentTab == "archived" && (
                    <FontAwesomeIcon
                      style={{ color: "black" }}
                      icon={faClockRotateLeft}
                    />
                  )}
                </Button>
              )}
            </div>
          </Card.Header>
          <Nav
            variant="underline"
            defaultActiveKey="active"
            onSelect={(k) => {
              setCurrentTab(k || "active");
              setSelectedIds([]);
              setActiveFilter(k == "active");
              setPageNumber(1);
              filterByActive(k == "active");
            }}
          >
            <Nav.Item>
              <Nav.Link eventKey="active" className="nav-link-business-unit">
                {`Active ${
                  counts?.active !== null && currentTab == "active"
                    ? `(${pageMeta.recordCount})`
                    : ""
                }`}
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                eventKey="archived"
                className="nav-link-business-unit"
              >
                {`Archived ${
                  counts?.archived !== null && currentTab == "archived"
                    ? `(${pageMeta.recordCount})`
                    : ""
                }`}
              </Nav.Link>
            </Nav.Item>
          </Nav>
          <DataTable
            data={businessUnits}
            columns={columns}
            selectable
            bordered
            striped
            hover
            totalPages={pageMeta.pageCount}
            totalRows={pageMeta.recordCount}
            currentPage={pageNumber}
            setCurrentPage={setPageNumber}
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
            selectedIds={selectedIds}
            setSelectedIds={setSelectedIds}
            paginationEnabled
            pageSizeEnabled
          />
        </Card>
        <ConfirmationModal
          title="Confirm Delete"
          message={`Are you sure that you want to ${currentTab == "active" ? "archive" : "restore"} ${selectedIds.length} business units?`}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
          show={showConfirmationModal}
          onHide={handleCancel}
        />
      </div>
    );
  }
};

export default List;
